/* Maison Septième Atelier — theme behaviour (no dependencies) */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animationsOn = window.msTheme ? window.msTheme.animations !== false : true;
  if (!animationsOn || reduceMotion) document.documentElement.classList.add('ms-no-anim');

  const focusableSel = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    const nodes = [...container.querySelectorAll(focusableSel)].filter((n) => n.offsetParent !== null);
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  /* Header: transparent -> solid, optional hide on scroll down */
  function initHeader() {
    const header = document.querySelector('[data-ms-header]');
    if (!header) return;
    const hideOnScroll = header.dataset.hideOnScroll === 'true';
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-solid', y > 40);
      if (hideOnScroll && !header.querySelector('.ms-nav__item.is-open')) {
        header.classList.toggle('is-hidden', y > lastY && y > 320);
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* Mega menu */
  function initMegaMenu() {
    const items = document.querySelectorAll('[data-mega-item]');
    if (!items.length) return;
    const canHover = window.matchMedia('(hover: hover)').matches;
    let closeTimer;
    const closeAll = (except) => {
      items.forEach((item) => {
        if (item === except) return;
        item.classList.remove('is-open');
        const t = item.querySelector('[data-mega-toggle]');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    };
    const open = (item) => {
      clearTimeout(closeTimer);
      closeAll(item);
      item.classList.add('is-open');
      item.querySelector('[data-mega-toggle]').setAttribute('aria-expanded', 'true');
    };
    items.forEach((item) => {
      const toggle = item.querySelector('[data-mega-toggle]');
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        item.classList.contains('is-open') ? closeAll() : open(item);
      });
      if (canHover) {
        item.addEventListener('mouseenter', () => open(item));
        item.addEventListener('mouseleave', () => { closeTimer = setTimeout(closeAll, 180); });
      }
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) { item.classList.remove('is-open'); toggle.setAttribute('aria-expanded', 'false'); }
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openItem = document.querySelector('[data-mega-item].is-open');
      if (openItem) { closeAll(); openItem.querySelector('[data-mega-toggle]').focus(); }
    });
    document.addEventListener('click', (e) => { if (!e.target.closest('[data-mega-item]')) closeAll(); });
  }

  /* Drawers (mobile menu, cart) */
  function initDrawers() {
    let lastTrigger = null;
    const openDrawer = (drawer, trigger) => {
      lastTrigger = trigger || null;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ms-locked');
      const focusTarget = drawer.querySelector('[data-drawer-focus]') || drawer.querySelector(focusableSel);
      if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    };
    const closeDrawer = (drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ms-locked');
      if (lastTrigger) { lastTrigger.setAttribute('aria-expanded', 'false'); lastTrigger.focus(); }
    };
    window.msOpenDrawer = (id, trigger) => { const d = document.getElementById(id); if (d) openDrawer(d, trigger); };
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        const drawer = document.getElementById(opener.dataset.drawerOpen);
        if (drawer) { e.preventDefault(); openDrawer(drawer, opener); }
        return;
      }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) { const drawer = closer.closest('.ms-drawer'); if (drawer) closeDrawer(drawer); }
    });
    document.addEventListener('keydown', (e) => {
      const drawer = document.querySelector('.ms-drawer.is-open');
      if (!drawer) return;
      if (e.key === 'Escape') closeDrawer(drawer);
      if (e.key === 'Tab') trapFocus(drawer, e);
    });
  }

  /* Search overlay */
  function initSearch() {
    const panel = document.querySelector('[data-ms-search]');
    const backdrop = document.querySelector('[data-ms-search-backdrop]');
    if (!panel) return;
    let trigger = null;
    const open = (t) => {
      trigger = t;
      panel.classList.add('is-open'); if (backdrop) backdrop.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      setTimeout(() => panel.querySelector('input[type="search"]').focus(), 80);
    };
    const close = () => {
      panel.classList.remove('is-open'); if (backdrop) backdrop.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      if (trigger) trigger.focus();
    };
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-search-open]');
      if (opener) { e.preventDefault(); open(opener); return; }
      if (e.target.closest('[data-search-close]') || e.target === backdrop) close();
    });
    document.addEventListener('keydown', (e) => {
      if (!panel.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') trapFocus(panel, e);
    });
  }

  /* Reveal on scroll */
  function initReveal(root = document) {
    const els = root.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || document.documentElement.classList.contains('ms-no-anim')) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  }

  /* Before / after */
  function initBeforeAfter(root = document) {
    root.querySelectorAll('[data-ba]').forEach((frame) => {
      const range = frame.querySelector('input[type="range"]');
      if (!range || range.dataset.bound) return;
      range.dataset.bound = 'true';
      const set = () => frame.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', set);
      set();
    });
  }

  /* Horizontal rails */
  function initRails(root = document) {
    root.querySelectorAll('[data-rail]').forEach((wrap) => {
      const track = wrap.querySelector('[data-rail-track]');
      if (!track || wrap.dataset.bound) return;
      wrap.dataset.bound = 'true';
      const step = () => (track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 24 : track.clientWidth);
      wrap.querySelectorAll('[data-rail-prev]').forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' })));
      wrap.querySelectorAll('[data-rail-next]').forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' })));
    });
  }

  /* Subtle parallax */
  function initParallax() {
    if (document.documentElement.classList.contains('ms-no-anim')) return;
    const els = [...document.querySelectorAll('[data-parallax]')];
    if (!els.length) return;
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const progress = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.transform = `translate3d(0, ${(progress * -40).toFixed(1)}px, 0) scale(1.08)`;
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  }

  function initForms() {
    document.querySelectorAll('[data-scroll-into-view]').forEach((el) => el.scrollIntoView({ block: 'center' }));
  }

  function init(root) {
    initReveal(root);
    initBeforeAfter(root);
    initRails(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initMegaMenu();
    initDrawers();
    initSearch();
    initParallax();
    initForms();
    init(document);
  });

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => { init(e.target); });
  document.addEventListener('shopify:block:select', (e) => { e.target.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible')); });

  window.msInit = init;
})();
